import React, { useRef, useState } from 'react';
import { 
  Box, 
  Breadcrumbs,
  Button, 
  Container,
  CircularProgress,
  Link,
  MenuItem,
  TextField, 
  Paper, 
  Typography, 
  Snackbar, 
  Alert, 
  Tooltip,
  IconButton,
  useMediaQuery,
  useTheme 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LogoutNavbar from '../../components/LogoutNavbar';
import GlobalSidebar from '../../components/GlobalSidebar';
import PostLoginFooter from '../../components/PostLoginFooter';
import { useNavigate } from 'react-router-dom';
import '../../styles/AdminDashboard.css';
import '../../styles/ClubForm.css';
import api from '../../api';

// Import the same icons used in other components for consistency
import {
  AccountCircle as ProfileIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
  Groups as ClubsIcon,
  Event as EventsIcon,
  PersonAdd as AddMemberIcon,
  Add as AddClubIcon,
} from '@mui/icons-material';

const statuses = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export default function AddClub() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [form, setForm] = useState({ 
    name: '', 
    description: '', 
    status: 'active'
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const mainContentRef = useRef(null);
  const [menuWidths, setMenuWidths] = useState({ status: null });
  const [openSelectField, setOpenSelectField] = useState(null);

  // Define sidebar items for mobile menu
  const sidebarItems = [
    { 
      label: 'Profile', 
      path: '/admin/profile', 
      action: () => navigate('/admin/profile'),
      icon: <ProfileIcon />
    },
    { 
      label: 'Manage Clubs', 
      path: '/admin/clubs', 
      action: () => navigate('/admin/clubs'),
      icon: <ClubsIcon />
    },
    { 
      label: 'Add Club', 
      path: '/admin/clubs/new', 
      action: () => navigate('/admin/clubs/new'),
      icon: <AddClubIcon />
    },
    { 
      label: 'Add Member', 
      path: '/admin/add-member', 
      action: () => navigate('/admin/add-member'),
      icon: <AddMemberIcon />
    },
    { 
      label: 'Manage Events', 
      path: '/admin/events', 
      action: () => navigate('/admin/events'),
      icon: <EventsIcon />
    },
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <SettingsIcon />
    },
  ];

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) {
      e.name = 'Please enter a club name (min 2 characters)';
    }
    if (!form.description || form.description.trim().length < 10) {
      e.description = 'Please enter a description (min 10 characters)';
    }
    return e;
  };

  const handleChange = (field) => (ev) => {
    setForm((s) => ({ ...s, [field]: ev.target.value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = async (ev) => {
    ev?.preventDefault?.();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        status: form.status,
      };

      await api.post('events/club-create-view/', payload);
      
      setOpenSnackbar(true);
      setTimeout(() => navigate('/admin/clubs'), 1000);
    } catch (err) {
      console.error('Error creating club:', err);
      
      // Handle different error scenarios
      if (err.response?.data) {
        const backendErrors = err.response.data;
        if (backendErrors.name) {
          setErrors({ name: backendErrors.name.join(', ') });
        } else if (backendErrors.description) {
          setErrors({ description: backendErrors.description.join(', ') });
        } else {
          setErrors({ form: 'Failed to create club. Please check your input.' });
        }
      } else {
        setErrors({ form: 'Unable to save. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (form.name || form.description) {
      if (window.confirm('Discard changes and go back?')) {
        navigate('/admin/clubs');
      }
    } else {
      navigate('/admin/clubs');
    }
  };

  const handleSelectOpen = (field) => (event) => {
    const width = event.currentTarget?.getBoundingClientRect().width;
    if (width) {
      setMenuWidths((prev) => ({ ...prev, [field]: width }));
    }
    setOpenSelectField(field);
  };

  const handleSelectClose = () => {
    setOpenSelectField(null);
  };

  const getMenuProps = (field) => {
    const width = menuWidths[field];
    return {
      disablePortal: false,
      disableScrollLock: true,
      container: mainContentRef.current,
      sx: {
        zIndex: 1300,
      },
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
      marginThreshold: null,
      transitionDuration: 120,
      PaperProps: {
        sx: {
          mt: 0,
          width: width || undefined,
          minWidth: width || undefined,
          maxWidth: width || undefined,
        },
      },
    };
  };

  return (
    <div className="admin-dashboard club-form add-club">
      {/* Pass sidebarItems to Navbar for mobile */}
      <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />
      <div className="dashboard-layout">
        {/* Only show sidebar on desktop */}
        {!isMobile && <GlobalSidebar />}
        <main className="dashboard-main">
          <Container ref={mainContentRef} maxWidth="xl" className="dashboard-container">
            <header className="cf-page-header">
              <Box className="cf-header-left">
                <Box className="cf-breadcrumb-row">
                  <Tooltip title="Back">
                    <IconButton onClick={handleCancel} className="cf-back-icon" size="medium" aria-label="Back">
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Breadcrumbs
                    className="cf-breadcrumbs"
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                  >
                    <Link
                      underline="hover"
                      color="inherit"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/admin/dashboard');
                      }}
                      href="/admin/dashboard"
                      className="cf-breadcrumb-link"
                    >
                      Dashboard
                    </Link>
                    <Link
                      underline="hover"
                      color="inherit"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/admin/clubs');
                      }}
                      href="/admin/clubs"
                      className="cf-breadcrumb-link"
                    >
                      Clubs
                    </Link>
                    <Typography color="text.primary" className="cf-breadcrumb-current">
                      Add
                    </Typography>
                  </Breadcrumbs>
                </Box>

                <Box className="cf-title-wrap">
                  <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" className="cf-page-title">
                    Add New Club
                  </Typography>
                  <Typography variant="body2" className="cf-page-subtitle" color="text.secondary">
                    Create a new club, add a description, and set its status.
                  </Typography>
                </Box>
              </Box>
            </header>

            <Paper className="cf-card" elevation={0}>
              <Box className="cf-card-header">
                <Box>
                  <Typography variant="h6" className="cf-card-title">Club Details</Typography>
                  <Typography className="cf-card-subtitle">Fill in the information below.</Typography>
                </Box>
              </Box>

              <Box component="form" onSubmit={handleSave} className="cf-form">
                <Box className="cf-form-grid">
                  <TextField
                    label="Club Name"
                    variant="outlined"
                    fullWidth
                    value={form.name}
                    onChange={handleChange('name')}
                    error={!!errors.name}
                    helperText={errors.name}
                    disabled={saving}
                    size={isMobile ? 'small' : 'medium'}
                  />

                  <TextField
                    select
                    label="Status"
                    variant="outlined"
                    fullWidth
                    value={form.status}
                    onChange={handleChange('status')}
                    disabled={saving}
                    size={isMobile ? 'small' : 'medium'}
                    SelectProps={{
                      MenuProps: getMenuProps('status'),
                      onOpen: handleSelectOpen('status'),
                      onClose: handleSelectClose,
                      open: openSelectField === 'status',
                    }}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    sx={{ minWidth: 160 }}
                  >
                    {statuses.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    className="cf-field-wide"
                    label="Description"
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={isMobile ? 4 : 5}
                    maxRows={10}
                    value={form.description}
                    onChange={handleChange('description')}
                    error={!!errors.description}
                    helperText={errors.description}
                    disabled={saving}
                    size={isMobile ? 'small' : 'medium'}
                    placeholder="Write a short description about what the club does…"
                  />
                </Box>

                {errors.form && (
                  <Alert severity="error" sx={{ marginTop: 2 }}>
                    {errors.form}
                  </Alert>
                )}

                <Box className="cf-actions">
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    className="cf-primary-btn"
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                  >
                    {saving ? 'Creating…' : 'Create Club'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={saving}
                    className="cf-secondary-btn"
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Paper>

            <Snackbar
              open={openSnackbar}
              autoHideDuration={2500}
              onClose={() => setOpenSnackbar(false)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert severity="success" sx={{ width: '100%' }}>
                Club "{form.name}" created successfully!
              </Alert>
            </Snackbar>
          </Container>
        </main>
      </div>
      <PostLoginFooter />
    </div>
  );
}